/* ===================== ADD / EDIT MODULE (TOPIC) MODAL =====================
   Module-first curriculum authoring: instructors create a module here, then
   attach lessons to it (CreateLessonModal). Deliberately minimal — title is
   the only required field; position auto-increments server-side.

     · create — POST /courses/:course/topics  { title, description? }
     · edit   — PUT  /topics/:topic — pass the curriculum `topic` and only
       the fields that actually changed (dirtyFields) are sent.

   Same form idiom, skin and 422 handling as the other academy modals.
   Success invalidates the shared course-detail read so the details page's
   curriculum (and the lesson form's module select) refresh, then closes. */
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@astro/api'
import { useForm } from '@astro/forms'

const inputCls =
  'w-full bg-stone-50 border rounded-xl px-4 py-3 text-sm text-astroDark focus:outline-none focus:border-goldAccent/50 focus:bg-white transition-colors'

function Field({ label, error, children }) {
  return (
    <div>
      <label className="block text-[9px] font-bold uppercase tracking-[0.15em] text-stone-600 mb-1.5">{label}</label>
      {children}
      {error && <p className="text-[10px] text-red-500 mt-1">{error}</p>}
    </div>
  )
}

export default function CreateTopicModal({ courseId, topic = null, onClose }) {
  const queryClient = useQueryClient()
  const [banner, setBanner] = useState(null)
  const isEdit = Boolean(topic?.id)

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, dirtyFields },
  } = useForm({
    defaultValues: {
      title: topic?.title ?? '',
      description: topic?.description ?? '',
    },
    mode: 'onTouched',
  })

  // Lock the page scroll and close on Escape while the dialog is up.
  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = prev
      window.removeEventListener('keydown', onKey)
    }
  }, [onClose])

  const mutation = useMutation({
    mutationFn: (payload) =>
      isEdit ? api.courses.updateTopic(topic.id, payload) : api.courses.createTopic(courseId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'course-detail', String(courseId)] })
      onClose()
    },
    onError: (err) => {
      const details = err?.details
      if (details && typeof details === 'object') {
        for (const [key, msgs] of Object.entries(details)) {
          setError(key, { type: 'server', message: Array.isArray(msgs) ? msgs[0] : String(msgs) })
        }
      }
      setBanner(err?.message || `Could not ${isEdit ? 'save' : 'create'} the module. Please try again.`)
    },
  })

  const onSubmit = handleSubmit((v) => {
    setBanner(null)
    if (isEdit) {
      // PUT only what changed — all topic fields are optional server-side.
      const payload = {}
      if (dirtyFields.title) payload.title = v.title.trim()
      if (dirtyFields.description) payload.description = v.description.trim()
      if (Object.keys(payload).length === 0) {
        onClose() // nothing changed — nothing to save
        return
      }
      mutation.mutate(payload)
      return
    }
    const payload = { title: v.title.trim() }
    if (v.description.trim()) payload.description = v.description.trim()
    mutation.mutate(payload)
  })

  const cls = (name) => `${inputCls} ${errors[name] ? 'border-red-300' : 'border-stone-200'}`

  const modal = (
    <div className="fixed inset-0 z-[1200] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-[#3D0A0A]/60 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />

      <div
        role="dialog"
        aria-modal="true"
        aria-label={isEdit ? 'Edit Module' : 'Add Module'}
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto p-6"
      >
        <div className="flex items-start justify-between gap-4 mb-5">
          <div>
            <span className="eyebrow text-amber-700">Vedic Academy</span>
            <h3 className="text-xl text-astroDark font-royal">{isEdit ? 'Edit Module' : 'Add Module'}</h3>
            <p className="text-xs text-stone-500 mt-0.5">
              {isEdit
                ? <>Update &ldquo;{topic.title}&rdquo;.</>
                : 'Modules organise the curriculum — lessons are added inside them.'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="w-8 h-8 shrink-0 rounded-full border border-stone-200 text-stone-500 hover:bg-stone-50 flex items-center justify-center text-sm font-bold"
          >
            ✕
          </button>
        </div>

        {banner && (
          <div className="mb-4 px-4 py-3 rounded-xl text-xs font-semibold bg-red-50 text-red-600 border border-red-100">
            {banner}
          </div>
        )}

        <form onSubmit={onSubmit} className="space-y-4">
          <Field label="Module Title" error={errors.title?.message}>
            <input
              className={cls('title')}
              placeholder="e.g. Introduction to Natal Charts"
              {...register('title', { required: 'Module title is required' })}
            />
          </Field>

          <Field label="Description (optional)" error={errors.description?.message}>
            <textarea
              rows={3}
              className={`${cls('description')} resize-none`}
              placeholder="What does this module cover?"
              {...register('description')}
            />
          </Field>

          <div className="flex items-center justify-end gap-2 pt-4 border-t border-stone-100">
            <button
              type="button"
              onClick={onClose}
              className="border border-stone-200 px-5 py-2.5 rounded-xl text-[10px] font-bold uppercase tracking-wider text-stone-700 hover:bg-stone-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="bg-linear-to-r from-goldAccent to-goldPrimary text-astroDark font-bold text-[10px] uppercase tracking-[0.15em] px-5 py-2.5 rounded-xl hover:shadow-lg hover:shadow-goldPrimary/25 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {mutation.isPending ? (isEdit ? 'Saving…' : 'Creating…') : isEdit ? 'Save Changes' : 'Create Module'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )

  // Portal to <body> so the overlay escapes the dashboard's stacking context
  // and paints above the site's fixed header (z-50).
  return typeof document === 'undefined' ? null : createPortal(modal, document.body)
}
